import classNames from 'classnames'
import styles from './LayoutModal.module.css'
import { Button } from '../../Button'
import { Heading } from '../../Heading'
import { IconButton } from '../../IconButton'
import { LayoutModalProps } from './types'
import { LayoutStack } from '../LayoutStack'
import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react'
import { useForwardedRef } from '../../../hooks'

function safeCloseDialogElement(element?: HTMLDialogElement | null): void {
  if (!element) return
  if (!('close' in element) || typeof element.close !== 'function') return
  element.close()
}

const LayoutModal = forwardRef<HTMLDialogElement, LayoutModalProps>(function LayoutModal(
  {
    id,
    isOpen,
    onClose,
    onOpen,
    onCloseComplete: onCloseComplete_,
    onOpenComplete: onOpenComplete_,
    title,
    customActions,
    children,
    manualFocusRef,
    closeText,
    closeOnClickOutside = true,
    hideHeader = false,
    hideActions = false,
    classNames: externalStyles = {},
  }: LayoutModalProps,
  ref_
) {
  const [modalRef, ref] = useForwardedRef(ref_)
  const defaultFocusRef = useRef<HTMLButtonElement>(null)
  const [animationCompleted, setAnimationCompleted] = useState(true)
  const getFocusRef = useCallback(() => {
    if (manualFocusRef) {
      return manualFocusRef
    }
    if (defaultFocusRef.current) {
      return defaultFocusRef
    }
    return modalRef
  }, [manualFocusRef, modalRef])
  const generatedId = useId()
  const modalId = id || generatedId

  const handleKeyPressed = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') return onClose(event)
      if (event.key !== 'Tab') return
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements?.[0] as HTMLElement
      const lastElement = focusableElements?.[focusableElements.length - 1] as HTMLElement
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement?.focus()
        return
      }
      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement?.focus()
      }
    },
    [modalRef, onClose]
  )

  const onCloseComplete = useCallback(() => {
    onCloseComplete_?.()
    setAnimationCompleted(true)
  }, [onCloseComplete_])

  const onOpenComplete = useCallback(() => {
    onOpenComplete_?.()
    setAnimationCompleted(true)
    modalRef.current?.removeAttribute('inert')
  }, [modalRef, onOpenComplete_])

  useEffect(() => {
    setAnimationCompleted(false)
    const modalElement = modalRef.current
    if (!modalElement) return
    if (isOpen) {
      // This can fail on Safari
      try {
        modalElement.showModal()
      } catch (e) {
        console.error(e)
      }
      const focusRef = getFocusRef()
      focusRef?.current?.focus()
      modalElement.removeEventListener('transitionend', onCloseComplete)
      modalElement.addEventListener('keydown', handleKeyPressed)
      modalElement.addEventListener('transitionend', onOpenComplete)
      onOpen?.()
    } else {
      modalElement.removeEventListener('keydown', handleKeyPressed)
      modalElement.removeEventListener('transitionend', onOpenComplete)
      modalElement.addEventListener('transitionend', onCloseComplete)
      // For whatever reason the element in the ref can be missing the close function at this point
      // TODO: Investigate why the close can be undefined at this point sometimes
      safeCloseDialogElement(modalElement)
      modalElement.setAttribute('inert', '')
    }
    return () => {
      modalElement?.removeEventListener('keydown', handleKeyPressed)
      modalElement.removeEventListener('transitionend', onCloseComplete)
      modalElement.removeEventListener('transitionend', onOpenComplete)
    }
  }, [getFocusRef, handleKeyPressed, isOpen, modalRef, onCloseComplete, onOpen, onOpenComplete])

  const handleClickOutside = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      const { current: el } = modalRef
      if (event.target === el) {
        onClose(event)
      }
    },
    [modalRef, onClose]
  )

  return (
    // Disabled because HTML dialog brings keyboard interaction with it
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      id={modalId}
      ref={ref}
      className={classNames(styles.modal, externalStyles.modal)}
      onCancel={onClose}
      onClick={closeOnClickOutside ? handleClickOutside : undefined}
      aria-labelledby={`modal-title-${modalId}`}
      aria-modal="true"
      title={title}
      tabIndex={-1}
    >
      <LayoutStack space="400" className={classNames(styles.modalInner, externalStyles.modalInner)}>
        {!hideHeader && (
          <div className={classNames(styles.modalHeader, externalStyles.titleContainer)}>
            <Heading level="heading-xs" as="h2" id={`modal-title-${modalId}`}>
              {title}
            </Heading>

            <IconButton
              kind="plain"
              className={styles.closeButton}
              onClick={onClose}
              ref={defaultFocusRef}
              type="button"
              iconName="x"
              iconProps={{ title: closeText, color: 'fg-content-primary' }}
            />
          </div>
        )}
        <div className={classNames(styles.modalContent, externalStyles.contentContainer)}>
          {(isOpen || !animationCompleted) && children}
        </div>
        {!hideActions && (
          <div className={classNames(styles.modalActions, externalStyles.actionsContainer)}>
            {!customActions ? (
              <Button onClick={onClose} type="button">
                {closeText}
              </Button>
            ) : (
              customActions
            )}
          </div>
        )}
      </LayoutStack>
      {/** In case of popups nested in the dialog element we need to move render the overlay in the dialog as well
       * the top-level layer otherwise will always remain on top
       */}
      {isOpen && (
        <>
          <div id="bp-portal-target" className={styles.portalTarget} role="alert"></div>
          <div id="bp-portal-target-inert" className={styles.portalTargetInert} role="alert"></div>
        </>
      )}
    </dialog>
  )
})

export default LayoutModal
